import { ApolloError, useQuery } from '@apollo/client';

import { ProductCarouselProps } from 'src/components/product-carousel';
import { RecentlyViewedProductsDocument } from 'src/gql/graphql';
import useDispensaryProducts from 'src/dispensary/hooks/use-dispensary-products';
import useDispensary from 'src/dispensary/hooks/use-dispensary';

type UseRecentlyViewedCarouselParams = {
  excludeProductId?: string;
};

type UseRecentlyViewedCarouselReturn = Omit<ProductCarouselProps, 'heading'>;

export function useRecentlyViewedCarousel({
  excludeProductId,
}: UseRecentlyViewedCarouselParams): UseRecentlyViewedCarouselReturn {
  const { dispensary } = useDispensary();

  const { data: productIdsData, loading: productIdsLoading, error: productIdsError } = useQuery(
    RecentlyViewedProductsDocument,
    {
      variables: {
        dispensaryId: dispensary?.id,
      },
      /*
       * The fetchPolicy of "network-only" is used in order to force the query to always be called due to the current user
       * being checked on the backend. Otherwise the previously cached products will appear.
       */
      fetchPolicy: `network-only`,
      skip: !dispensary?.id,
    }
  );

  let productIds = productIdsData?.getRecentlyViewedProducts?.map((product) => product.id) ?? [];

  if (excludeProductId) {
    productIds = productIds.filter((productId) => productId !== excludeProductId);
  }

  const { products, loading: productsLoading } = useDispensaryProducts({
    filters: {
      dispensaryId: dispensary?.id,
      productIds,
      status: 'Active',
      removeProductsBelowOptionThresholds: true,
      sortBy: 'inputOrder',
    },
    page: 1,
    perPage: 25,
    skip: productIdsLoading || productIdsError instanceof ApolloError || !productIds.length,
  });

  return {
    products,
    loading: productIdsLoading || productsLoading,
  };
}
